import './SubstitutionBanner.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { MdClose, MdEdit, MdSchedule, MdSwapHoriz } from 'react-icons/md';
import Button from '@srm/ui/button';
import { useUi } from '@srm/ui/provider';
import dayjs from '@srm/ui/lib/dayjs';
import { RouteNames } from '@/router/router.types';
import { UserService } from '@services/UserService';
import { IUserAssistants } from '@models/IUserAssistants';
import { useTypedSelector } from '@hooks/useTypedSelector';
import UserName from '@molecules/UserName/UserName';
import { sendNotification } from '@srm/ui/lib/notifications';
import { safeFormatDate } from '@/utils/date';
import { toLocalDate } from '@srm/ui/lib/toLocalDate';
import { plural } from '@srm/ui/lib/pluralize';
import { deriveSubstitution, IActiveSubstitution } from './substitution';
import { publishSupportSubstitution } from '@providers/ScreenShareProvider/supportSubstitution';

interface ISubstitutionBannerProps {
    /** Закрыть меню — при переходе в раздел настройки замещения. */
    onClose: () => void;
}

/** Подпись срока под именем заместителя. */
function periodText(sub: IActiveSubstitution): string {
    if (sub.kind === 'permanent') return 'Постоянное замещение';

    const startStr = safeFormatDate(sub.startDate, 'DD.MM.YYYY');
    const endStr = safeFormatDate(sub.endDate, 'DD.MM.YYYY');

    if (sub.scheduled) {
        if (startStr && endStr) return `с ${startStr} до ${endStr}`;
        return startStr ? `начнётся ${startStr}` : 'Временное замещение';
    }

    if (endStr) {
        const daysLeft = dayjs(toLocalDate(sub.endDate!)).startOf('day').diff(dayjs().startOf('day'), 'day');
        if (daysLeft <= 0) return `до ${endStr} · заканчивается сегодня`;
        return `до ${endStr} · осталось ${daysLeft} ${plural(daysLeft, ['день', 'дня', 'дней'])}`;
    }
    return 'Временное замещение';
}

function eyebrowText(sub: IActiveSubstitution): string {
    // Запланированный ВРИО ещё не действует — «Вас замещает» тут неверно, тон будущего.
    return sub.kind === 'temporary' && sub.scheduled ? 'Будет замещать' : 'Вас замещает';
}

function badgeLabel(sub: IActiveSubstitution): string {
    return sub.kind === 'permanent' ? 'Постоянно' : 'Временно';
}

/**
 * Полоса-статус активного замещения в меню пользователя. Показывается только когда
 * сотрудник сам назначил себе заместителя (getAssistant): постоянного (уполномоченный)
 * или временного (ВРИО). Даёт перейти в настройку и снять замещение в два шага.
 * Данные грузятся на маунте — Mantine Dialog размонтирует содержимое закрытым, поэтому
 * это происходит при каждом открытии меню (всегда свежее состояние).
 */
const SubstitutionBanner: React.FC<ISubstitutionBannerProps> = ({ onClose }) => {
    const canManage = useTypedSelector((s) => s.auth.user?.canView?.setAssistantSimple ?? false);
    const { navigate } = useUi();

    const [raw, setRaw] = useState<IUserAssistants | null>(null);
    const [sub, setSub] = useState<IActiveSubstitution | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [removing, setRemoving] = useState(false);

    useEffect(() => {
        if (!canManage) return;
        let alive = true;
        UserService.getAssistant()
            .then((r) => {
                if (!alive) return;
                setRaw(r.data);
                const derived = deriveSubstitution(r.data);
                setSub(derived);
                // Пакет диагностики поддержки (§43) берёт уже известное замещение, своего запроса не делает.
                publishSupportSubstitution(derived);
            })
            .catch(() => {
                if (alive) setSub(null);
            });
        return () => {
            alive = false;
        };
    }, [canManage]);

    const onEdit = useCallback(() => {
        onClose();
        void navigate(RouteNames.SET_ASSISTANT_SIMPLE);
    }, [navigate, onClose]);

    const onConfirmRemove = useCallback(() => {
        setRemoving(true);
        // Снятие = сохранение с пустыми заместителями: бэк чистит и постоянного,
        // и временного при пустых AssistantId/DummyId (AccountService.SetAssistants).
        const cleared: IUserAssistants = {
            userId: raw?.userId ?? '',
            assistantId: undefined,
            assistantName: undefined,
            dummyId: undefined,
            dummyName: undefined,
            dummyStartDate: undefined,
            dummyEndDate: undefined,
        };
        void UserService.setAssistant(cleared)
            .then(() => {
                setSub(null);
                setConfirming(false);
                sendNotification({ message: 'Замещение снято', variant: 'green' });
            })
            .catch((e: unknown) => {
                sendNotification({
                    title: 'Не удалось снять замещение',
                    message: e instanceof Error ? e.message : 'Попробуйте ещё раз.',
                    variant: 'red',
                });
            })
            .finally(() => setRemoving(false));
    }, [raw]);

    if (!sub) return null;

    const modifier = sub.kind === 'permanent' ? 'perm' : 'temp';

    if (confirming) {
        return (
            <div className={`rf-subst rf-subst--${modifier} rf-subst--confirming`}>
                <div className="rf-subst__confirm">
                    <span className="rf-subst__confirm-q">Снять замещение?</span>
                    <Button variant="secondary" size="sm" onClick={() => setConfirming(false)} disabled={removing}>
                        Отмена
                    </Button>
                    <Button variant="light" tone="danger" size="sm" onClick={onConfirmRemove} disabled={removing}>
                        Снять
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className={`rf-subst rf-subst--${modifier}`}>
            <div className="rf-subst__top">
                <span className="rf-subst__ic" aria-hidden>
                    <MdSwapHoriz size={19} />
                </span>
                <div className="rf-subst__txt">
                    <div className="rf-subst__head">
                        <span className="rf-subst__eb">{eyebrowText(sub)}</span>
                        <span className="rf-subst__badge">
                            {sub.kind === 'temporary' ? (
                                <MdSchedule size={13} aria-hidden />
                            ) : (
                                <span className="rf-subst__dot" aria-hidden />
                            )}
                            {badgeLabel(sub)}
                        </span>
                    </div>
                    <div className="rf-subst__name">
                        <UserName userKey={sub.key} name={sub.name} position="left" />
                    </div>
                    <div className="rf-subst__meta">{periodText(sub)}</div>
                </div>
            </div>
            <div className="rf-subst__acts">
                <button type="button" className="rf-subst__lnk rf-subst__lnk--brand" onClick={onEdit}>
                    <MdEdit size={15} aria-hidden />
                    Изменить
                </button>
                <span className="rf-subst__sep" aria-hidden>
                    ·
                </span>
                <button
                    type="button"
                    className="rf-subst__lnk rf-subst__lnk--danger"
                    onClick={() => setConfirming(true)}
                >
                    <MdClose size={15} aria-hidden />
                    Снять
                </button>
            </div>
        </div>
    );
};

export default SubstitutionBanner;
